import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { IconTick } from "@/newComponents/icon/Icon";
import Heading from "@/newComponents/typography/Heading";
import { useUIStateStore } from "@/store/uiStateStore";

import { Button } from "../../../../../../newComponents/button/Button";
import { DialogFooter } from "../../../../../../newComponents/dialog/Dialog";
import { FormCheckbox } from "../../../../../../newComponents/input/FormCheckbox";
import { FormInput } from "../../../../../../newComponents/input/FormInput";
import { ScrollArea } from "../../../../../../newComponents/scrollArea/ScrollArea";
import Text from "../../../../../../newComponents/typography/Text";
import useWriterStore from "../store/writerStore";

type SelectExistingWritersModalProps = {
  onClose: () => void;
};

export default function SelectExistingWritersModal({ onClose }: SelectExistingWritersModalProps) {
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { availableWriters, selectedWriters, addSelectedWriter } = useWriterStore();
  const [tempSelectedWriters, setTempSelectedWriters] = useState<Set<string>>(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const { t } = useTranslation("album");

  const handleWriterToggle = (writercode: string) => {
    setTempSelectedWriters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(writercode)) {
        newSet.delete(writercode);
      }
      else {
        newSet.add(writercode);
      }
      return newSet;
    });
  };

  // Filter out already selected writers and apply search
  const availableWritersFiltered = useMemo(() => {
    return availableWriters.filter((writer) => {
      // Filter out already selected writers
      const notAlreadySelected = !selectedWriters.some(selected => selected.writercode === writer.writercode);

      // Apply search filter
      if (!searchQuery.trim()) {
        return notAlreadySelected;
      }

      // Create display name for searching
      let displayName = writer.writername;
      if (!displayName || displayName.trim() === "") {
        const parts = [writer.firstname, writer.lastname].filter(Boolean);
        displayName = parts.join(" ");
      }

      // Search in name and CAE/IPI number
      const searchLower = searchQuery.toLowerCase();
      const nameMatch = displayName.toLowerCase().includes(searchLower);
      const caeIpiMatch = writer.caeipinumber?.toLowerCase().includes(searchLower) ?? false;

      return notAlreadySelected && (nameMatch || caeIpiMatch);
    }).reverse();
  }, [availableWriters, selectedWriters, searchQuery]);

  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      const allWriterCodes = availableWritersFiltered.map(writer => writer.writercode);
      setTempSelectedWriters(new Set(allWriterCodes));
    }
    else {
      setTempSelectedWriters(new Set());
    }
  }, [availableWritersFiltered]);

  const handleConfirm = () => {
    const writersToAdd = availableWriters
      .filter(writer => tempSelectedWriters.has(writer.writercode))
      .map((writer) => {
        // Create display name from firstname/lastname if writername is not available
        let displayName = writer.writername;
        if (!displayName || displayName.trim() === "") {
          const parts = [writer.firstname, writer.lastname].filter(Boolean);
          displayName = parts.join(" ");
        }

        return {
          writercode: writer.writercode,
          writername: displayName,
          caeipinumber: writer.caeipinumber,
        };
      });

    writersToAdd.forEach((writer) => {
      addSelectedWriter(writer);
    });

    // toast.success(`Added ${writersToAdd.length} writer(s) to the album`);
    toast.success(t("added-writers", { count: writersToAdd.length }));
    onClose();
  };

  // If availableWriters is empty, show a message to load writers first
  if (availableWriters.length === 0) {
    return (
      <div className={`space-y-6 ${isTunecore ? "text-white" : ""}`}>
        <div>
          <Text as="p">
            {t("no-writers-available")}
          </Text>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            label={t("close")}
            onClick={onClose}
          />
        </DialogFooter>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isTunecore ? "text-white" : ""}`}>
      <div className="space-y-4">
        <Heading as="h3" className={`text-lg font-semibold ${isTunecore ? "text-white" : ""}`}>
          {t("select-existing-writers")}
        </Heading>
        <Text as="p">
          <span>{t("select-existing-writers-description1")}</span>
          <br />
          <span>{t("select-existing-writers-description2")}</span>
        </Text>
        {/* Stats and Select All */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Text as="p" className="">
              {t("available-writers", { count: availableWritersFiltered.length })}
            </Text>
            <Text as="p" className="font-semibold ">
              {/* Selected: */}
              {t("writers-selected", { count: tempSelectedWriters.size })}

            </Text>
          </div>
          <div className="flex items-center">
            <FormCheckbox
              label={t("select-all") ?? "Select All"}
              checked={availableWritersFiltered.length > 0 && tempSelectedWriters.size === availableWritersFiltered.length}
              onChange={e => handleSelectAll(e.target.checked)}
              className="w-4 h-4"
            />
          </div>
        </div>
      </div>

      <FormInput
        label={t("search-writers") ?? "Search Writers"}
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        placeholder={t("search-writers-placeholder") ?? "Search by name or CAE/IPI number..."}
      />
      {/* Writers List */}
      <ScrollArea className={`h-[500px] border rounded-lg ${isTunecore ? "border-tunecore-light-grey" : "border-sentric-mid-grey"}`}>
        <div className="p-4 space-y-3">
          {availableWritersFiltered.length === 0 ? (
            <div className="py-8 text-center">
              <Text as="p" className={isTunecore ? "text-white" : "text-black"}>
                {t("no-writers-found")}
              </Text>
            </div>
          ) : (
            availableWritersFiltered.map((writer) => {
              // Create display name from firstname/lastname if writername is not available
              let displayName = writer.writername;
              if (!displayName || displayName.trim() === "") {
                const parts = [writer.firstname, writer.lastname].filter(Boolean);
                displayName = parts.join(" ");
              }
              return (
                <button
                  key={writer.writercode}
                  type="button"
                  className={`w-full p-4 border rounded-lg cursor-pointer transition-colors text-left ${
                    tempSelectedWriters.has(writer.writercode)
                      ? isTunecore
                        ? "bg-tunecore-light-grey border-tunecore-light-grey"
                        : "bg-sentric-mid-grey border-sentric-mid-grey"
                      : isTunecore
                        ? "bg-[#282828] border-tunecore-light-grey hover:bg-tunecore-light-grey"
                        : "bg-white border-sentric-mid-grey hover:bg-primary-button-hover"
                  }`}
                  onClick={() => handleWriterToggle(writer.writercode)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Text as="p" className={`font-semibold ${isTunecore ? "text-white" : ""}`}>
                        {displayName}
                      </Text>
                      <Text as="p" className={`text-sm ${isTunecore ? "text-white" : ""}`}>
                        {/* CAE/IPI: */}
                        {t("cae-ipi") ?? "CAE/IPI:"}
                        {" "}
                        {writer.caeipinumber ?? "N/A"}
                      </Text>
                    </div>
                    {tempSelectedWriters.has(writer.writercode) && (
                      <IconTick className={`h-8 w-8 ${isTunecore ? "stroke-white" : "stroke-black"}`} />
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>
      </ScrollArea>

      <DialogFooter>
        <Button
          variant="darkSecondary"
          // label="Cancel"
          label={t("cancel") ?? "Cancel"}
          onClick={onClose}
          className={isTunecore ? "border-tunecore-light-grey text-white hover:bg-tunecore-light-grey" : ""}
        />

        <Button
          variant={isTunecore ? "primary" : "secondary"}
          // label={`Add ${tempSelectedWriters.size} Writer(s)`}
          label={t("add-writers", { count: tempSelectedWriters.size }) ?? `Add ${tempSelectedWriters.size} Writer(s)`}
          onClick={handleConfirm}
          disabled={tempSelectedWriters.size === 0}
        />

      </DialogFooter>
    </div>
  );
}
